import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { Pencil } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { DatePicker } from '@/components/tasks/DatePicker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  CheckInObjectiveResponse,
  CheckInObjectiveType,
  ProviderTreatmentStatus,
} from '@/services/types/treatment-types';

import { ObjectiveResponseToggle } from './ObjectiveResponseToggle';
import {
  getObjectiveResponseNote,
} from './objectiveUi';
import type { EditCheckInFormValues } from './types';

interface ObjectiveCardProps {
  index: number;
  disabled?: boolean;
  /** Map from providerId to provider_updates array index */
  providerIndexMap: Map<string, number>;
}

export const ObjectiveCard: React.FC<ObjectiveCardProps> = ({
  index,
  disabled = false,
  providerIndexMap,
}) => {
  const { control, watch, setValue } =
    useFormContext<EditCheckInFormValues>();
  const response = watch(`objectives.${index}.response`);
  const responseNote = watch(`objectives.${index}.responseNote`);
  const label = watch(`objectives.${index}.label`);
  const objectiveType = watch(`objectives.${index}.objectiveType`);
  const providerId = watch(`objectives.${index}.providerId`);
  const isPending = response === CheckInObjectiveResponse.PENDING;
  const isYes = response === CheckInObjectiveResponse.YES;
  const isNo = response === CheckInObjectiveResponse.NO;
  const isCNC = response === CheckInObjectiveResponse.COULD_NOT_COLLECT;

  const isStillTreating =
    objectiveType === CheckInObjectiveType.CONFIRM_STILL_TREATING;
  const isFrequency =
    objectiveType === CheckInObjectiveType.CONFIRM_TREATMENT_FREQUENCY;
  const isFirstVisit =
    objectiveType === CheckInObjectiveType.CONFIRM_FIRST_VISIT;
  const isAppointmentDate =
    objectiveType === CheckInObjectiveType.CONFIRM_APPOINTMENT_DATE;
  const isCompletionDate =
    objectiveType === CheckInObjectiveType.CONFIRM_COMPLETION_DATE;

  const providerIndex =
    providerId != null ? providerIndexMap.get(providerId) : undefined;

  // Read current provider data for pre-populating inline inputs
  const providerFrequencyText =
    providerIndex !== undefined
      ? watch(`provider_updates.${providerIndex}.newTreatmentFrequencyText`)
      : undefined;
  const providerDate =
    providerIndex !== undefined
      ? watch(`provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`)
      : undefined;
  const providerFirstTreatmentDate =
    providerIndex !== undefined
      ? watch(`provider_updates.${providerIndex}.newFirstTreatmentDate`)
      : undefined;

  const providerIsFirstAppointment =
    providerIndex !== undefined
      ? watch(`provider_updates.${providerIndex}.isFirstAppointment`)
      : false;

  // Read current provider status for auto-sync guard
  const currentStatus =
    providerIndex !== undefined
      ? watch(`provider_updates.${providerIndex}.newTreatmentStatus`)
      : undefined;

  // Track whether we've already cleared a past appointment date (one-time mount check)
  const clearedPastDateRef = useRef(false);

  // Free-text frequency: editing state — start in edit mode when no value exists
  const [isEditingFrequency, setIsEditingFrequency] = useState(!providerFrequencyText);

  /** Auto-set response to YES when data is entered inline */
  const autoConfirm = useCallback(() => {
    if (response !== CheckInObjectiveResponse.YES) {
      setValue(`objectives.${index}.response`, CheckInObjectiveResponse.YES, {
        shouldDirty: true,
      });
    }
  }, [response, setValue, index]);

  const handleFrequencyTextChange = useCallback(
    (value: string) => {
      autoConfirm();
      if (providerIndex !== undefined) {
        setValue(
          `provider_updates.${providerIndex}.newTreatmentFrequencyText`,
          value || null,
          { shouldDirty: true },
        );
      }
    },
    [autoConfirm, providerIndex, setValue],
  );

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      autoConfirm();
      const formattedDate = date ? format(date, 'yyyy-MM-dd') : undefined;
      if (providerIndex !== undefined) {
        setValue(
          `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
          formattedDate ?? null,
          { shouldDirty: true },
        );
        // Keep legacy draft state in sync when first-visit was already confirmed.
        if (providerIsFirstAppointment) {
          setValue(
            `provider_updates.${providerIndex}.newFirstTreatmentDate`,
            formattedDate ?? null,
            { shouldDirty: true },
          );
        }
      }
    },
    [autoConfirm, providerIndex, setValue, providerIsFirstAppointment],
  );

  const handleFirstVisitDateChange = useCallback(
    (date: Date | undefined) => {
      const formattedDate = date ? format(date, 'yyyy-MM-dd') : undefined;
      if (providerIndex !== undefined) {
        setValue(
          `provider_updates.${providerIndex}.newFirstTreatmentDate`,
          formattedDate ?? null,
          { shouldDirty: true },
        );
        setValue(
          `provider_updates.${providerIndex}.isFirstAppointment`,
          response === CheckInObjectiveResponse.YES && !!formattedDate,
          { shouldDirty: true },
        );
        if (response === CheckInObjectiveResponse.YES) {
          setValue(
            `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
            formattedDate ?? null,
            { shouldDirty: true },
          );
        }
      }
    },
    [providerIndex, response, setValue],
  );

  const setObjectiveResponse = useCallback(
    (nextResponse: CheckInObjectiveResponse) => {
      if (providerIndex !== undefined && isFirstVisit) {
        if (nextResponse === CheckInObjectiveResponse.YES) {
          const firstVisitDate = providerFirstTreatmentDate ?? null;
          setValue(
            `provider_updates.${providerIndex}.isFirstAppointment`,
            true,
            { shouldDirty: true },
          );
          setValue(
            `provider_updates.${providerIndex}.newFirstTreatmentDate`,
            firstVisitDate,
            { shouldDirty: true },
          );
          if (firstVisitDate) {
            setValue(
              `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
              firstVisitDate,
              { shouldDirty: true },
            );
          }
        } else if (nextResponse === CheckInObjectiveResponse.NO) {
          setValue(
            `provider_updates.${providerIndex}.isFirstAppointment`,
            false,
            { shouldDirty: true },
          );
          if (providerIsFirstAppointment && providerDate === providerFirstTreatmentDate) {
            setValue(
              `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
              null,
              { shouldDirty: true },
            );
          }
        } else {
          setValue(
            `provider_updates.${providerIndex}.newFirstTreatmentDate`,
            null,
            { shouldDirty: true },
          );
          setValue(
            `provider_updates.${providerIndex}.isFirstAppointment`,
            false,
            { shouldDirty: true },
          );
          if (providerIsFirstAppointment && providerDate === providerFirstTreatmentDate) {
            setValue(
              `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
              null,
              { shouldDirty: true },
            );
          }
        }
      }

      setValue(`objectives.${index}.response`, nextResponse, {
        shouldDirty: true,
      });
      setValue(
        `objectives.${index}.responseNote`,
        getObjectiveResponseNote(nextResponse, responseNote),
        { shouldDirty: nextResponse === CheckInObjectiveResponse.COULD_NOT_COLLECT },
      );
    },
    [
      index,
      isFirstVisit,
      providerDate,
      providerFirstTreatmentDate,
      providerIndex,
      providerIsFirstAppointment,
      responseNote,
      setValue,
    ],
  );

  // Auto-confirm appointment date when the date is already pre-populated.
  // Without this, pre-filled dates leave the objective PENDING and block submission.
  useEffect(() => {
    if (!isAppointmentDate || !isPending || !providerDate) return;
    autoConfirm();
  }, [isAppointmentDate, isPending, providerDate, autoConfirm]);

  useEffect(() => {
    if (!isFirstVisit || !isPending || !providerFirstTreatmentDate) return;
    autoConfirm();
  }, [isFirstVisit, isPending, providerFirstTreatmentDate, autoConfirm]);

  // Note: we intentionally do NOT re-apply the default "I didn't ask" note
  // when the user clears the response note. The default is only pre-filled
  // when the user first clicks Unsure/N/A (handled by setObjectiveResponse).

  // Auto-confirm frequency when pre-populated with existing text value.
  useEffect(() => {
    if (!isFrequency || !isPending) return;
    if (providerFrequencyText) {
      autoConfirm();
    }
  }, [isFrequency, isPending, providerFrequencyText, autoConfirm]);

  // Clear past appointment dates on mount — stale past dates shouldn't be pre-filled.
  useEffect(() => {
    if (!isAppointmentDate || providerIndex === undefined || clearedPastDateRef.current) return;
    clearedPastDateRef.current = true;
    if (providerDate) {
      const dateValue = new Date(`${providerDate}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dateValue < today) {
        setValue(
          `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
          null,
          { shouldDirty: false },
        );
      }
    }
  }, [isAppointmentDate, providerIndex, providerDate, setValue]);

  // Still-treating status sync is handled by ObjectivesSection (always mounted)
  // to avoid issues with card collapsing/unmounting during No→Yes transitions.

  // Auto-sync for completion date: set COMPLETED when confirmed YES,
  // but only if status hasn't been manually set.
  useEffect(() => {
    if (providerIndex === undefined || !isYes || !isCompletionDate) return;
    if (currentStatus && currentStatus !== ProviderTreatmentStatus.UNKNOWN)
      return;

    setValue(
      `provider_updates.${providerIndex}.newTreatmentStatus`,
      ProviderTreatmentStatus.COMPLETED,
      { shouldDirty: true },
    );
  }, [isYes, isCompletionDate, providerIndex, setValue, currentStatus]);

  // -----------------------------------------------------------------------
  // Inline content renderers
  // -----------------------------------------------------------------------

  /** Free-text frequency input with read-only display + "Change" button */
  const renderFrequencyInline = () => {
    // If editing or no existing value, show the text input
    if (isEditingFrequency || !providerFrequencyText) {
      return (
        <div className="flex items-center gap-1.5">
          <Input
            type="text"
            className="h-7 w-[160px] text-xs"
            value={providerFrequencyText ?? ''}
            disabled={disabled}
            autoFocus={isEditingFrequency}
            onChange={(e) => handleFrequencyTextChange(e.target.value)}
            onBlur={() => {
              if (providerFrequencyText) setIsEditingFrequency(false);
            }}
            placeholder="e.g. 2x per week"
          />
        </div>
      );
    }

    // Read-only display with "Change" button
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium text-zinc-700">
          {providerFrequencyText}
        </span>
        <button
          type="button"
          className="inline-flex items-center gap-0.5 text-[11px] text-zinc-500 hover:text-zinc-700"
          disabled={disabled}
          onClick={() => setIsEditingFrequency(true)}
        >
          <Pencil className="h-3 w-3" />
          Change
        </button>
      </div>
    );
  };

  /** Appointment date: inline date picker or CNC state */
  const renderAppointmentDateInline = () => {
    if (isCNC) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-zinc-500">Not scheduled</span>
          <button
            type="button"
            className="text-[11px] text-zinc-500 underline hover:text-zinc-700"
            disabled={disabled}
            onClick={() => {
              setValue(
                `objectives.${index}.response`,
                CheckInObjectiveResponse.PENDING,
                { shouldDirty: true },
              );
              setValue(`objectives.${index}.responseNote`, null, {
                shouldDirty: true,
              });
            }}
          >
            Enter a date
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <DatePicker
          className="h-7 text-xs w-[160px] justify-start"
          date={
            providerDate ? new Date(`${providerDate}T00:00:00`) : undefined
          }
          {...{
            onDateChange: handleDateChange,
          }}
          placeholder="Select date"
          align="center"
          side="bottom"
        />
        <button
          type="button"
          className="text-[11px] text-zinc-500 hover:text-zinc-700 shrink-0"
          disabled={disabled}
          onClick={() => {
            if (providerIndex !== undefined) {
              setValue(
                `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
                null,
                { shouldDirty: true },
              );
              setValue(
                `provider_updates.${providerIndex}.newFirstTreatmentDate`,
                null,
                { shouldDirty: true },
              );
              setValue(
                `provider_updates.${providerIndex}.isFirstAppointment`,
                false,
                { shouldDirty: true },
              );
            }
            setObjectiveResponse(CheckInObjectiveResponse.COULD_NOT_COLLECT);
          }}
        >
          N/A
        </button>
      </div>
    );
  };

  const renderFirstVisitDateInline = () => (
    <DatePicker
      className="h-7 text-xs w-[160px] justify-start"
      date={
        providerFirstTreatmentDate
          ? new Date(`${providerFirstTreatmentDate}T00:00:00`)
          : undefined
      }
      {...{
        onDateChange: handleFirstVisitDateChange,
      }}
      placeholder="First visit date"
      align="center"
      side="bottom"
    />
  );

  /** Still-treating = No: inline end date */
  const renderStillTreatingEndDate = () => (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-medium text-zinc-500">Ended:</span>
      <DatePicker
        className="h-7 text-xs w-[160px] justify-start"
        date={
          providerDate ? new Date(`${providerDate}T00:00:00`) : undefined
        }
        {...{
          onDateChange: (date: Date | undefined) => {
            const formattedDate = date
              ? format(date, 'yyyy-MM-dd')
              : undefined;
            if (providerIndex !== undefined) {
              setValue(
                `provider_updates.${providerIndex}.newTreatmentStatusOccurredAt`,
                formattedDate ?? null,
                { shouldDirty: true },
              );
            }
          },
        }}
        placeholder="When? (optional)"
        align="center"
        side="bottom"
      />
    </div>
  );

  /** Completion date: inline date picker on YES */
  const renderCompletionDateInline = () => (
    <DatePicker
      className="h-7 text-xs w-[160px] justify-start"
      date={
        providerDate ? new Date(`${providerDate}T00:00:00`) : undefined
      }
      {...{
        onDateChange: handleDateChange,
      }}
      placeholder="Completion date"
      align="center"
      side="bottom"
    />
  );

  // -----------------------------------------------------------------------
  // Main render — single compact row per objective
  // -----------------------------------------------------------------------

  // Frequency and appointment date have their own interaction patterns (no toggle)
  const showStandardToggle = !isFrequency && !isAppointmentDate;

  // CNC/DNA notes: show reason textarea for non-appointment CNC responses
  const showStandardCNCNote = isCNC && !isAppointmentDate;

  return (
    <div className="mx-[-4px] rounded-md px-1 py-1 transition-colors duration-150 hover:bg-zinc-50/80 focus-within:bg-zinc-50/80">
      {/* Main row: label + inline control */}
      <div className="flex items-center justify-between gap-2 min-h-[28px]">
        <span className="text-xs font-medium text-zinc-700 shrink-0">
          {label}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {/* Standard toggle (still treating, completion date) */}
          {showStandardToggle && (
            <Controller
              name={`objectives.${index}.response`}
              control={control}
              render={({ field }) => (
                <ObjectiveResponseToggle
                  value={field.value}
                  onChange={setObjectiveResponse}
                  disabled={disabled}
                  needsAttention={isPending}
                  threeState={isStillTreating || isFirstVisit}
                />
              )}
            />
          )}

          {/* Frequency: inline confirm/dropdown */}
          {isFrequency && renderFrequencyInline()}

          {/* Appointment date: inline date picker */}
          {isAppointmentDate && renderAppointmentDateInline()}
        </div>
      </div>

      {/* Revealed content below the row */}
      <AnimatePresence initial={false}>
        {(isYes || isNo) && isFirstVisit && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1 pl-2">
              {renderFirstVisitDateInline()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion date: show date picker when YES */}
      <AnimatePresence initial={false}>
        {isYes && isCompletionDate && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1 pl-2">
              {renderCompletionDateInline()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Still-treating = No → end date picker */}
      <AnimatePresence initial={false}>
        {isNo && isStillTreating && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1 pl-2">
              {renderStillTreatingEndDate()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CNC note for appointment date */}
      <AnimatePresence initial={false}>
        {isCNC && isAppointmentDate && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1 pl-2">
              <Controller
                name={`objectives.${index}.responseNote`}
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={1}
                    placeholder="Why couldn't you collect this?"
                    className="text-xs"
                    disabled={disabled}
                  />
                )}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CNC/DNA reason note */}
      <AnimatePresence initial={false}>
        {showStandardCNCNote && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1 pl-2">
              <Controller
                name={`objectives.${index}.responseNote`}
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={1}
                    placeholder={
                      isStillTreating
                        ? 'Why could you not assess this?'
                        : 'Why couldn\'t you collect this?'
                    }
                    className="text-xs"
                    disabled={disabled}
                  />
                )}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
